import React, { useEffect, useState, useCallback, useRef } from "react";
import { aliasGmailPlus } from "../lib/emailAlias";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  useRealtimeTable,
  useVisiblePolling,
  REALTIME_DOWN_POLL_MS,
} from "../lib/useRealtimeTable";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Save, Trash2, RotateCcw, UserCog } from "lucide-react";
import { formatDateTime } from "../lib/format";
import { apiErrorMessage } from "../lib/inventoryApi";
import { WHITELIST_NO_SCOPE_ROLES } from "../lib/roles";
import { cakupanPeran, isianLengkap, badanUbahJabatan, kalimatUbahJabatan } from "../lib/ubahJabatan";

// Daftar role TIDAK LAGI diketik di sini. Ia dibaca dari `GET /api/roles`,
// sumber yang sama dengan tabel `roles` yang dipakai backend untuk memvalidasi
// `role_code`.
//
// KENAPA (FINANCE-02). Daftar yang diketik tangan di sini berisi TUJUH role dan
// kehilangan FINANCE. Selama backend ikut menolak FINANCE, dropdown yang tidak
// menawarkannya tidak pernah terlihat salah. Begitu FINANCE masuk
// `VALID_ROLE_CODES`, layar ini berubah jadi satu-satunya yang menghalangi:
// endpoint siap menerima Finance, tapi Owner tidak punya cara memilihnya.
//
// Membaca dari backend menutup seluruh kelas cacat itu, bukan satu kejadiannya
// — role kesembilan nanti muncul sendiri tanpa ada yang perlu ingat menyunting
// berkas ini. Polanya sudah terbukti jalan di repo ini: `DevRoleSwitcher` sudah
// memuat daftarnya lewat `api.get("/roles")`.
const NO_SCOPE_ROLES = new Set(WHITELIST_NO_SCOPE_ROLES);

// Label jangkauan untuk anggota NO_SCOPE_ROLES, dipakai DUA tempat di layar
// ini (baris tersimpan dan baris draft) — satu fungsi supaya predikatnya
// tidak ditulis ulang dan bisa melenceng lagi seperti kejadian 25 Agustus
// 2026 (baris tersimpan sempat menulis "Semua region" untuk semua anggota,
// baris draft sudah membedakan OWNER).
//
// Cabang dua arah di sini aman sekalipun bentuk yang sama pernah jadi cacat
// di layar ini juga (lihat commit 43c002c/372cf3a). Bedanya pada apa yang
// diklaim cabang "selain OWNER": dulu ia mengklaim IDENTITAS ("Designer"),
// benar untuk satu dari empat anggota; di sini ia mengklaim JANGKAUAN, benar
// untuk seluruh anggota NO_SCOPE_ROLES selain OWNER — sebab masuk daftar itu
// BERARTI barisnya region+store NULL, alias tidak terikat satu region.
// Predikatnya sama dengan pembedaan yang sedang digambar, jadi tidak bisa
// basi diam-diam seperti pendahulunya.
function noScopeRegionLabel(roleCode) {
  return roleCode === "OWNER" ? "Semua wilayah" : "Lintas wilayah";
}

function emptyDraft(roleCode) {
  return { email: "", nama_tampilan: "", role_code: roleCode, region_id: null, store_id: null, _draft: true };
}

export default function Users() {
  const { profile } = useAuth();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // { ROLE_CODE: [drafts...] }

  // T109 (2 Nov 2026): `setLoading(true)` DICABUT dari sini — pola yang sama dengan FADetail (2 Sep 2026). Polling 25 detik
  // dan kanal realtime memanggil load() berulang; tiap panggilan dulu mengganti SELURUH layar dengan "Memuat..." — isian
  // email baru dan dialog Ubah jabatan yang sedang dibuka ikut lenyap. Muatan PERTAMA tetap "Memuat..." lewat nilai awal
  // useState(true); penyegaran latar berjalan diam, dan gagalnya tidak memunculkan galat tiap 25 detik.
  const sudahDimuat = useRef(false);
  const load = useCallback(async () => {
    try {
      const [wl, r, s, rl] = await Promise.all([
        api.get("/users/whitelist"),
        api.get("/regions"),
        api.get("/stores"),
        api.get("/roles"),
      ]);
      setItems(wl.data);
      setRegions(r.data);
      setStores(s.data);
      setRoles(rl.data || []);
      sudahDimuat.current = true;
    } catch (e) {
      if (!sudahDimuat.current) toast.error(apiErrorMessage(e, "Gagal memuat data pengguna."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Jeda polling layar admin. 25 detik MENIRU NotificationsBell, jeda paling
  // longgar yang dipakai repo ini — daftar whitelist berubah jarang, dan hanya
  // Owner yang bisa membukanya.
  const WHITELIST_POLL_MS = 25000;

  // Sama seperti Settings: sebelumnya `.subscribe()` telanjang tanpa callback
  // status, nol penyambungan ulang, dan layar ini NOL polling.
  const rtConnected = useRealtimeTable("user_email_whitelist", load);
  useVisiblePolling(
    load,
    rtConnected ? WHITELIST_POLL_MS : REALTIME_DOWN_POLL_MS
  );

  // Tampilan (Titipan auditor 26 Okt 2026): bawaan hanya akun AKTIF; yang nonaktif dibuka lewat satu tombol.
  const [tampilNonaktif, setTampilNonaktif] = useState(false);
  const jumlahNonaktif = items.filter((it) => !it.is_active).length;
  const grouped = roles.map((r) => ({
    role: r,
    rows: items.filter((it) => it.role?.code === r.code && (tampilNonaktif || it.is_active)),
  }));

  const ensureMinDrafts = (roleCode, existingCount) => {
    const cur = drafts[roleCode] || [];
    const need = Math.max(0, 2 - existingCount - cur.length);
    if (need > 0) {
      const add = Array.from({ length: need }, () => emptyDraft(roleCode));
      setDrafts((d) => ({ ...d, [roleCode]: [...cur, ...add] }));
    }
  };

  useEffect(() => {
    if (!loading) {
      roles.forEach((r) => {
        const existing = items.filter((it) => it.role?.code === r.code).length;
        ensureMinDrafts(r.code, existing);
      });
    }
    // eslint-disable-next-line
  }, [loading, items.length, roles.length]);

  const addDraft = (roleCode) => {
    setDrafts((d) => ({ ...d, [roleCode]: [...(d[roleCode] || []), emptyDraft(roleCode)] }));
  };

  const updateDraft = (roleCode, idx, patch) => {
    setDrafts((d) => {
      const arr = [...(d[roleCode] || [])];
      arr[idx] = { ...arr[idx], ...patch };
      // role-specific resets
      if (NO_SCOPE_ROLES.has(roleCode)) { arr[idx].region_id = null; arr[idx].store_id = null; }
      if (roleCode === "KEPALA_CABANG") { arr[idx].store_id = null; }
      if (patch.region_id !== undefined && arr[idx].store_id) {
        const st = stores.find((s) => s.id === arr[idx].store_id);
        if (!st || st.region_id !== patch.region_id) arr[idx].store_id = null;
      }
      return { ...d, [roleCode]: arr };
    });
  };

  const saveDraft = async (roleCode, idx) => {
    const draft = (drafts[roleCode] || [])[idx];
    if (!draft?.email?.trim()) { toast.error("Email wajib diisi"); return; }
    // T58: nama tampilan WAJIB di layar — nama inilah yang tercatat di jejak, layar, dan dokumen (bukan nama Google).
    const nt = (draft.nama_tampilan || "").trim().replace(/\s+/g, " ");
    if (nt.length < 2 || nt.length > 80) { toast.error("Nama tampilan wajib diisi (2 sampai 80 huruf)"); return; }
    try {
      await api.post("/users/whitelist", {
        email: draft.email.trim().toLowerCase(),
        nama_tampilan: nt,
        role_code: roleCode,
        region_id: draft.region_id,
        store_id: draft.store_id,
      });
      toast.success(`Email ditambahkan ke daftar izin masuk (${roles.find((r) => r.code === roleCode)?.name || "peran terpilih"})`);
      setDrafts((d) => {
        const arr = [...(d[roleCode] || [])];
        arr.splice(idx, 1);
        return { ...d, [roleCode]: arr };
      });
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menambahkan email."));
    }
  };

  const toggleActive = async (row) => {
    try {
      if (row.is_active) {
        await api.delete(`/users/whitelist/${row.id}`);
        toast.success("Akun dinonaktifkan");
      } else {
        await api.patch(`/users/whitelist/${row.id}`, { is_active: true });
        toast.success("Akun diaktifkan kembali");
      }
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengubah keadaan akun."));
    }
  };

  const isSelf = (row) => row?.email === profile?.email;
  const storesForRegion = (regionId) => stores.filter((s) => s.region_id === regionId);

  // T56 Ubah jabatan — karyawan pindah jabatan dengan email yang sama (kalimat Owner 25 Okt 2026). Backend
  // menyinkronkan profilnya seketika, jadi permintaan berikutnya dari orang itu sudah memakai jabatan baru.
  const [ubah, setUbah] = useState(null); // { row, d: {role_code, region_id, store_id, is_active} }
  const [menyimpan, setMenyimpan] = useState(false);
  const bukaUbah = (row) => setUbah({
    row,
    d: { role_code: row.role?.code || "", region_id: row.region_id || null, store_id: row.store_id || null,
         is_active: !!row.is_active, nama_tampilan: row.nama_tampilan || "" },
  });
  const aturUbah = (patch) => setUbah((u) => {
    const d = { ...u.d, ...patch };
    const c = cakupanPeran(d.role_code);
    if (c === "TANPA") { d.region_id = null; d.store_id = null; }
    if (c === "REGION") d.store_id = null;
    if (d.store_id && stores.find((s) => s.id === d.store_id)?.region_id !== d.region_id) d.store_id = null;
    return { ...u, d };
  });
  const simpanUbah = async () => {
    setMenyimpan(true);
    try {
      await api.patch(`/users/whitelist/${ubah.row.id}`, badanUbahJabatan(ubah.d, ubah.row));
      toast.success(`Jabatan ${ubah.row.email} sudah diubah. Berlaku pada langkah berikutnya orang itu di aplikasi.`);
      setUbah(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Jabatan belum berubah."));
    } finally {
      setMenyimpan(false);
    }
  };

  if (loading) return <div data-testid="users-loading">Memuat...</div>;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">User Management</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tambah, ubah jabatan, atau nonaktifkan email per peran. Hanya Owner yang dapat mengakses halaman ini.
        </p>
        {jumlahNonaktif > 0 && (
          <Button
            data-testid="tampil-nonaktif"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => setTampilNonaktif((v) => !v)}
          >
            {tampilNonaktif ? "Sembunyikan yang nonaktif" : `Tampilkan yang nonaktif (${jumlahNonaktif})`}
          </Button>
        )}
      </div>

      {grouped.map(({ role, rows }) => (
        <Card key={role.code} className="p-5" data-testid={`role-section-${role.code}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold">{role.name}</h2>
            <Button
              data-testid={`add-email-${role.code}`}
              variant="outline"
              size="sm"
              onClick={() => addDraft(role.code)}
            >
              <Plus size={16} className="mr-1" /> Tambah email
            </Button>
          </div>

          <div className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.id}
                data-testid={`whitelist-row-${row.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center bg-muted/50 p-3 rounded-md"
              >
                <div className="md:col-span-3 text-sm break-all">
                  {/* T58: nama tampilan = nama pelaku di jejak/dokumen; kosong ditandai supaya Owner melengkapinya. */}
                  <div className="font-medium" data-testid={`nama-tampilan-${row.id}`}>
                    {row.nama_tampilan || <span className="italic text-warning-soft-foreground">Nama tampilan belum diisi</span>}
                  </div>
                  <div className="text-xs text-muted-foreground" data-testid={`email-${row.id}`}>{row.email}</div>
                </div>
                <div className="md:col-span-1 text-xs text-muted-foreground">
                  {row.region?.name || (NO_SCOPE_ROLES.has(role.code) ? noScopeRegionLabel(role.code) : "—")}
                </div>
                <div className="md:col-span-1 text-xs text-muted-foreground">
                  {row.store?.name || (role.code === "OWNER" || role.code === "KEPALA_CABANG" || role.code === "DESIGNER_EDITOR" ? "—" : "—")}
                </div>
                <div className="md:col-span-2 text-xs text-muted-foreground" data-testid={`last-login-${row.id}`}>
                  {row.last_login_at ? formatDateTime(row.last_login_at) : <span className="text-muted-foreground italic">Belum pernah login</span>}
                </div>
                <div className="md:col-span-1">
                  {row.is_active
                    ? <Badge data-testid={`status-${row.id}`} className={row.has_logged_in ? "bg-success text-success-foreground" : "bg-warning-soft text-warning-soft-foreground"}>{row.has_logged_in ? "Aktif" : "Belum masuk"}</Badge>
                    : <Badge data-testid={`status-${row.id}`} variant="secondary">Nonaktif</Badge>}
                </div>
                <div className="md:col-span-4 flex flex-wrap justify-end gap-2">
                  <Button
                    data-testid={`ubah-jabatan-${row.id}`}
                    variant="outline"
                    size="sm"
                    disabled={isSelf(row)}
                    title={isSelf(row) ? "Jabatan Owner sendiri tidak bisa diubah dari sini." : undefined}
                    onClick={() => bukaUbah(row)}
                  >
                    <UserCog size={14} className="mr-1" />Ubah jabatan
                  </Button>
                  <Button
                    data-testid={`toggle-active-${row.id}`}
                    variant={row.is_active ? "destructive" : "default"}
                    size="sm"
                    disabled={isSelf(row) && row.is_active}
                    onClick={() => toggleActive(row)}
                  >
                    {row.is_active ? <><Trash2 size={14} className="mr-1" />Nonaktifkan</> : <><RotateCcw size={14} className="mr-1" />Aktifkan</>}
                  </Button>
                </div>
              </div>
            ))}

            {(drafts[role.code] || []).map((d, idx) => (
              <div
                key={`draft-${role.code}-${idx}`}
                data-testid={`draft-row-${role.code}-${idx}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center bg-card border border-dashed border-border p-3 rounded-md"
              >
                <div className="md:col-span-4">
                  <Input
                    data-testid={`draft-email-${role.code}-${idx}`}
                    placeholder="email@example.com"
                    value={d.email}
                    onChange={(e) => updateDraft(role.code, idx, { email: e.target.value })}
                  />
                  <Input
                    className="mt-1"
                    data-testid={`draft-nama-${role.code}-${idx}`}
                    placeholder="Nama tampilan (wajib), mis. Rina — CS Samarinda"
                    maxLength={80}
                    value={d.nama_tampilan || ""}
                    onChange={(e) => updateDraft(role.code, idx, { nama_tampilan: e.target.value })}
                  />
                  {aliasGmailPlus(d.email) && (
                    <p className="text-xs text-warning-soft-foreground mt-1" data-testid={`draft-alias-${role.code}-${idx}`}>
                      Alamat Gmail bertanda "+" adalah alias. Saat masuk lewat Google, yang terbaca alamat aslinya,
                      jadi akun ini tidak akan bisa masuk. Pakai alamat Gmail asli tanpa "+".
                    </p>
                  )}
                </div>
                <div className="md:col-span-3">
                  {NO_SCOPE_ROLES.has(role.code) ? (
                    // Baris ini menerangkan JANGKAUAN saja, lewat
                    // noScopeRegionLabel() di atas. NAMA peran sudah dicetak
                    // judul kartu di atas dari `role.name` (API); mengulangnya
                    // di sini melahirkan sumber kebenaran kedua yang bisa
                    // melenceng dari seed `roles.name` di DB — persis yang
                    // terjadi ketika baris ini pernah menulis "Desainer"
                    // sementara judulnya menulis "Designer/Editor".
                    <div className="text-xs text-muted-foreground italic">{noScopeRegionLabel(role.code)}</div>
                  ) : (
                    <Select
                      value={d.region_id || ""}
                      onValueChange={(v) => updateDraft(role.code, idx, { region_id: v || null })}
                    >
                      <SelectTrigger data-testid={`draft-region-${role.code}-${idx}`}>
                        <SelectValue placeholder="Pilih wilayah" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="md:col-span-3">
                  {NO_SCOPE_ROLES.has(role.code) || role.code === "KEPALA_CABANG" ? (
                    <div className="text-xs text-muted-foreground italic">
                      {role.code === "KEPALA_CABANG" ? "Baca-tulis seluruh wilayah" : "Semua cabang"}
                    </div>
                  ) : (
                    <Select
                      value={d.store_id || ""}
                      onValueChange={(v) => updateDraft(role.code, idx, { store_id: v || null })}
                      disabled={!d.region_id}
                    >
                      <SelectTrigger data-testid={`draft-store-${role.code}-${idx}`}>
                        <SelectValue placeholder={d.region_id ? "Pilih cabang" : "Pilih wilayah dulu"} />
                      </SelectTrigger>
                      <SelectContent>
                        {storesForRegion(d.region_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <Button
                    data-testid={`save-draft-${role.code}-${idx}`}
                    size="sm"
                    onClick={() => saveDraft(role.code, idx)}
                  >
                    <Save size={14} className="mr-1" /> Simpan
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}

      <Dialog open={!!ubah} onOpenChange={(o) => { if (!o && !menyimpan) setUbah(null); }}>
        {ubah && (
          <DialogContent data-testid="dialog-ubah-jabatan">
            <DialogHeader>
              <DialogTitle>Ubah jabatan</DialogTitle>
              <DialogDescription>{ubah.row.email}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Nama tampilan</div>
                <Input data-testid="ubah-nama-tampilan" maxLength={80} value={ubah.d.nama_tampilan}
                       placeholder="Nama yang tercatat di jejak dan dokumen"
                       onChange={(e) => aturUbah({ nama_tampilan: e.target.value })} />
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Peran</div>
                <Select value={ubah.d.role_code} onValueChange={(v) => aturUbah({ role_code: v })}>
                  <SelectTrigger data-testid="ubah-peran"><SelectValue placeholder="Pilih peran" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {cakupanPeran(ubah.d.role_code) === "TANPA" ? (
                <div className="text-xs text-muted-foreground italic">
                  {ubah.d.role_code === "OWNER" ? "Semua wilayah dan cabang"
                    : ubah.d.role_code === "PEMANTAU" ? "Hanya melihat — semua wilayah dan cabang, tanpa tombol aksi"
                    : "Lintas wilayah — tanpa wilayah dan cabang"}
                </div>
              ) : (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Wilayah</div>
                  <Select value={ubah.d.region_id || ""} onValueChange={(v) => aturUbah({ region_id: v || null })}>
                    <SelectTrigger data-testid="ubah-region"><SelectValue placeholder="Pilih wilayah" /></SelectTrigger>
                    <SelectContent>
                      {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {cakupanPeran(ubah.d.role_code) === "TOKO" && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Cabang</div>
                  <Select value={ubah.d.store_id || ""} onValueChange={(v) => aturUbah({ store_id: v || null })}
                          disabled={!ubah.d.region_id}>
                    <SelectTrigger data-testid="ubah-toko">
                      <SelectValue placeholder={ubah.d.region_id ? "Pilih cabang" : "Pilih wilayah dulu"} />
                    </SelectTrigger>
                    <SelectContent>
                      {storesForRegion(ubah.d.region_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {cakupanPeran(ubah.d.role_code) === "REGION" && (
                <div className="text-xs text-muted-foreground italic">Baca-tulis seluruh wilayah</div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <Switch data-testid="ubah-aktif" checked={ubah.d.is_active}
                        onCheckedChange={(v) => aturUbah({ is_active: v })} />
                {ubah.d.is_active ? "Aktif — boleh masuk" : "Nonaktif — tidak boleh masuk"}
              </label>
              {isianLengkap(ubah.d) && (
                <p className="text-sm font-medium" data-testid="ubah-kalimat">
                  {kalimatUbahJabatan(ubah.row, ubah.d, roles, regions, stores)}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUbah(null)} disabled={menyimpan}>Batal</Button>
              <Button data-testid="ubah-simpan" onClick={simpanUbah} disabled={menyimpan || !isianLengkap(ubah.d)}>
                {menyimpan ? "Menyimpan..." : "Ya, ubah"}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
